import { useCallback, useState } from "react";
import type { TipperCreatePayload } from "@/customTypings/tipper/Tipper";
import type { TipperDeletedRestoreAvailableConflict } from "@/utils/tipper/tipperCreateConflict";
import {
    TIPPER_CREATE_CONFLICT,
    TipperCreateConflictError,
    isTipperDeletedRestoreAvailableConflict,
} from "@/utils/tipper/tipperCreateConflict";
import { DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, showToastError, showToastSuccess } from "@/utils";

type UseTipperRestoreOnCreateConflictOptions<TDetail extends { id: string }> = {
    restoreDeletedWithCreateValues: (payload: TipperCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail) => void;
};

export function useTipperRestoreOnCreateConflict<TDetail extends { id: string }>({
    restoreDeletedWithCreateValues,
    onRestored,
}: UseTipperRestoreOnCreateConflictOptions<TDetail>) {
    const [restoreConflict, setRestoreConflict] =
        useState<TipperDeletedRestoreAvailableConflict | null>(null);
    const [pendingRestoreForm, setPendingRestoreForm] = useState<TipperCreatePayload | null>(null);
    const [restoringDeleted, setRestoringDeleted] = useState(false);

    const handleCreateError = useCallback((err: unknown): boolean => {
        if (err instanceof TipperCreateConflictError) {
            if (
                isTipperDeletedRestoreAvailableConflict(
                    err.conflict,
                    TIPPER_CREATE_CONFLICT.deletedRestoreAvailable
                )
            ) {
                setRestoreConflict(err.conflict);
                return true;
            }
        }
        return false;
    }, []);

    const clearRestoreConflict = useCallback(() => {
        setRestoreConflict(null);
        setPendingRestoreForm(null);
    }, []);

    const confirmRestore = useCallback(
        async (confirmed: boolean) => {
            if (!confirmed || !restoreConflict || !pendingRestoreForm) {
                clearRestoreConflict();
                return;
            }
            setRestoringDeleted(true);
            try {
                const restored = await restoreDeletedWithCreateValues({
                    id: restoreConflict.existingId,
                    ...pendingRestoreForm,
                });
                const label = restoreConflict.existingName;
                clearRestoreConflict();
                showToastSuccess(`"${label}" restored successfully!`);
                onRestored(restored);
            } catch {
                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
            } finally {
                setRestoringDeleted(false);
            }
        },
        [restoreConflict, pendingRestoreForm, restoreDeletedWithCreateValues, onRestored, clearRestoreConflict]
    );

    const restoreModalProps = {
        show: restoreConflict !== null && pendingRestoreForm !== null,
        title: "Restore deleted tipper?",
        body: restoreConflict
            ? "A deleted tipper matching these details already exists. Restore it using the details you entered?"
            : "",
        buttonSuccessText: restoringDeleted ? "Restoring…" : "Yes",
        buttonCancelText: "No" as const,
    };

    return {
        handleCreateError,
        setPendingRestoreForm,
        confirmRestore,
        restoreModalProps,
    };
}
